import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { Combobox, Popover } from '@headlessui/react'
import clsx from 'clsx'
import { SearchableField } from '@purposely/common/src/insights'

import { Bubble } from '../../../../components/Bubble/Bubble'
import {
  SearchQuery,
  useGlobalSearchQueries,
} from '../../hooks/useGlobalSearchQueries'
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { CookieType, cookies } from '../../../../state/cookies'
import { useTrackingContext } from '../../../../analytics/useTracking'
import { trackInsightSearched } from '../../../../analytics/tracking'
import { useGlobalState } from '../../../../state/v2/store'


interface SearchOption {
  fieldKey: SearchableField
  label: string
  description?: string | React.ReactNode
  type: 'includes' | 'excludes'
}

const MetaAdsLink = ({ children }: { children: React.ReactNode }) => (
  <a
    href="https://www.facebook.com/business/ads-guide/update/image/facebook-feed/traffic"
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: 'lightblue', textDecoration: 'underline' }}
    onMouseDown={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
  >
    {children}
  </a>
);

const searchOptions: SearchOption[] = [
  {
    fieldKey: SearchableField.Name,
    label: 'Ad name',
    type: 'includes',
  },
  {
    fieldKey: SearchableField.Tag,
    label: 'Tags',
    type: 'includes',
    description: 'Custom segment of ads created in Ads Summary (table view)',
  },
  {
    fieldKey: SearchableField.AdId,
    label: 'Ad id',
    type: 'includes',
  },
  {
    fieldKey: SearchableField.PrimaryText,
    label: 'Primary text',
    type: 'includes',
    description: (
      <>
      Main text copy of the ad, <MetaAdsLink>as shown here.</MetaAdsLink>
      </>
    )
  },
  {
    fieldKey: SearchableField.LabelName,
    label: 'Label name',
    type: 'includes',
  },
  {
    fieldKey: SearchableField.AdLabel,
    label: 'Ad labels',
    type: 'includes',
  },
  {
    fieldKey: SearchableField.CampaignName,
    label: 'Campaign name',
    type: 'includes',
  },
  {
    fieldKey: SearchableField.CampaignId,
    label: 'Campaign id',
    type: 'includes',
  },
  {
    fieldKey: SearchableField.AdSetName,
    label: 'Ad set name',
    type: 'includes',
  },
  {
    fieldKey: SearchableField.AdSetId,
    label: 'Ad set id',
    type: 'includes',
  },
  {
    fieldKey: SearchableField.HookText,
    label: 'Hook text',
    type: 'includes',
    description: 'Text overlay on an image, or within the first 3 seconds of a video',
  },
  {
    fieldKey: SearchableField.Headline,
    label: 'Headline',
    type: 'includes',
    description: (
      <>
        Short snippet of text appearing below visual asset for Meta In-feed placements, <MetaAdsLink>as shown here.</MetaAdsLink>
      </>
    )
  },
  {
    fieldKey: SearchableField.DominantColor,
    label: 'Primary color',
    type: 'includes',
    description: 'Dominant color detected in the ad creative',
  },
]

const fieldKeyLabelLookup = searchOptions.reduce<Record<string, string>>(
  (acc, curr) => ({ ...acc, [curr.fieldKey]: curr.label }),
  {}
)

export function GlobalSearch() {
  const [query, setQuery] = useState('')
  const [searchQueries, setSearchQueries] = useGlobalSearchQueries()
  const [insightsView, setInsightsView] = useState(
    () => cookies.get(CookieType.INSIGHTS_VIEW) || 'table'
  )
  const ctx = useTrackingContext()
  const brandId = useGlobalState((s) => s.brand?.[0]?.id ?? '')

  const isInsights = window.location.href.includes('/insights')

  useEffect(() => {
    const syncInsightsView = () => {
      setInsightsView(cookies.get(CookieType.INSIGHTS_VIEW) || 'table')
    }

    window.addEventListener('insights-view-change', syncInsightsView)
    return () => {
      window.removeEventListener('insights-view-change', syncInsightsView)
    }
  }, [])

  const allowedOptions = searchOptions.filter(option => {
    if (isInsights) {
      return option.fieldKey !== SearchableField.AdLabel;
    } else {
      return option.fieldKey !== SearchableField.LabelName;
    }
  });
  
  const handleToggleInclusion = useCallback(
    (opt: SearchQuery) => {
      setSearchQueries((prev: SearchQuery[] | null | undefined) => {
        const next: SearchQuery[] = []
        for (const sq of prev || []) {
          if (sq.fieldKey === opt.fieldKey && sq.value === opt.value) {
            next.push({
              ...sq,
              type: sq.type === 'includes' ? 'excludes' : 'includes',
            })
          } else {
            next.push(sq)
          }
        }
        return next
      })
    },
    [setSearchQueries]
  )

  const handleAddition = useCallback(
    (opt: string) => {
      setQuery('')

      setSearchQueries((prev: SearchQuery[] = []) => [
        ...prev,
        {
          fieldKey: opt as SearchableField,
          value: query,
          type: 'includes',
        } as SearchQuery,
      ])
      trackInsightSearched({ brand_id: brandId, account_id: ctx.accountId, search_query: query, search_field: opt }, ctx)
    },
    [query, setSearchQueries, brandId, ctx]
  )

  const handleSearchQueryRemove = useCallback(
    (opt: SearchQuery) => {
      setSearchQueries((prev: SearchQuery[] = []) => {
        const next = []
        for (const sq of prev) {
          if (sq.fieldKey === opt.fieldKey && sq.value === opt.value) {
            continue
          } else {
            next.push(sq)
          }
        }
        return next
      })
    },
    [setSearchQueries]
  )

  const clearSearchQueries = useCallback(() => {
    setSearchQueries([])
  }, [setSearchQueries])

  const handleSearchInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.currentTarget.value)
    },
    []
  )
  
  const allowedSearchQueries = searchQueries?.filter(searchQuery => {
    return allowedOptions.some(option => option.fieldKey === searchQuery.fieldKey);
  });

  if (isInsights && insightsView !== 'table') {
    return null
  }

  return (
    <>
      <Combobox
        as="div"
        value={null}
        onChange={handleAddition}
        className="w-96"
      >
        <div className="relative">
          <Combobox.Input
            className="focus:ring-[#0b6d5e] w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
            value={query}
            onChange={handleSearchInputChange}
            displayValue={(option: SearchOption | null) => option?.label || ''}
            placeholder="Search and filter"
            autoComplete="off"
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <MagnifyingGlassIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </Combobox.Button>

          {
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {allowedOptions.length > 1 && <div className="px-3 py-2 font-bold">
                Suggested searches and exclusions
              </div>}
              {allowedOptions.map((opt) => (
                <Combobox.Option
                  key={opt.fieldKey}
                  value={opt.fieldKey}
                  className={({ active }) =>
                    clsx(
                      'relative cursor-pointer select-none py-2 pl-3 pr-9',
                      active ? 'bg-[#f5f5f5] text-gray-900' : 'text-gray-900'
                    )
                  }
                >
                  {({ active, selected }) => (
                    <>
                      <div className="block truncate">
                        {opt.label}{' '}
                        {opt.description && (
                          <CustomTooltip title={opt.description} placement="bottom">
                            <HelpOutlineIcon
                              sx={{
                                cursor: 'help',
                                color: 'text.secondary',
                                fontSize: 14,
                                position: 'relative',
                                top: '-1px',
                              }}
                            />
                          </CustomTooltip>
                        )}{' '}
                        contains{' '}
                        <span className="bg-trueBlue inline-block rounded px-2 font-medium text-white">
                          {query}
                        </span>
                      </div>

                      {selected && (
                        <span
                          className={clsx(
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                            active ? 'text-gray-900' : 'text-gray-900'
                          )}
                        ></span>
                      )}
                    </>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          }
        </div>
        {!!allowedSearchQueries?.length && (
          <Popover className="relative">
            <div className="mt-2 flex items-center justify-between">
              <Popover.Button className="text-trueBlue text-sm hover:underline">
                <div>{allowedSearchQueries.length} active filter(s)</div>
              </Popover.Button>

              <button
                className="text-trueBlue text-sm hover:underline"
                onClick={clearSearchQueries}
              >
                Clear
              </button>
            </div>

            <Popover.Panel className="absolute z-10 bg-white p-3 shadow">
              <div className="mt-2 flex max-w-full flex-wrap gap-2">
                {allowedSearchQueries?.map((sq: SearchQuery) => (
                  <Bubble
                    variant="grey"
                    key={`${sq.fieldKey}:${sq.value}`}
                    className="rounded"
                  >
                    <div className="flex items-center gap-2 text-xs">
                      <span>{fieldKeyLabelLookup[sq.fieldKey]}</span>
                      <button
                        className="rounded bg-gray-500 p-1 hover:bg-gray-300"
                        onClick={() => handleToggleInclusion(sq)}
                      >
                        {sq.type}
                      </button>
                      <span>"{sq.value}"</span>
                      <button
                        className="flex items-center gap-2 rounded hover:bg-red-300"
                        onClick={() => handleSearchQueryRemove(sq)}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </Bubble>
                ))}
              </div>
            </Popover.Panel>
          </Popover>
        )}
      </Combobox>
    </>
  )
}